import React from "react";
import { Link } from "react-router-dom";

import {
  Target,
  Users,
  UserRound,
  Stethoscope,
  Phone,
  CalendarDays,
  ShieldCheck,
  HeartPulse,
  Smartphone,
  Mail,
  ArrowRight,
  UserPlus,
  LogIn,
  CheckCircle2,
  ClipboardList,
} from "lucide-react";

import { FaGooglePlay, FaApple } from "react-icons/fa";

const founders = [
  {
    name: "Gurukumar Reddy",
    role: "Founder",
  },
];

const AboutUs = () => {
  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-blue-50 via-white to-cyan-100 text-gray-800">
      {/* =====================================================
          MAIN RESPONSIVE CONTAINER
      ====================================================== */}

      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12">
        {/* =====================================================
            HEADER
        ====================================================== */}

        <header className="py-4 sm:py-5">
          <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-sm border border-blue-100 px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between gap-4">
              {/* =================================================
                  LOGO
              ================================================= */}

              <Link to="/" className="flex shrink-0 items-center">
                <img
                  src="/logo.jpeg"
                  alt="MR Health"
                  title="MR Health"
                  className="
                    h-12
                    w-auto
                    object-contain
                    sm:h-14
                    md:h-16
                  "
                />
              </Link>

              {/* =================================================
                  HOME BUTTON
              ================================================= */}

              <Link
                to="/"
                className="
                  flex-shrink-0
                  px-4
                  sm:px-5
                  py-2
                  rounded-lg
                  bg-blue-600
                  text-white
                  text-sm
                  sm:text-base
                  font-medium
                  hover:bg-blue-700
                  transition
                "
              >
                Home
              </Link>
            </div>
          </div>
        </header>

        {/* =====================================================
            HERO SECTION
        ====================================================== */}

        <section className="py-10 sm:py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
            {/* =================================================
                LEFT SIDE
            ================================================= */}

            <div className="w-full min-w-0">
              <div
                className="
                  inline-flex
                  items-center
                  gap-2
                  px-3
                  sm:px-4
                  py-2
                  bg-blue-100
                  text-blue-700
                  rounded-full
                  text-xs
                  sm:text-sm
                  font-semibold
                "
              >
                <HeartPulse className="w-4 h-4 flex-shrink-0" />
                Digital Healthcare Platform
              </div>

              <h1
                className="
                  mt-5
                  text-3xl
                  sm:text-4xl
                  md:text-5xl
                  lg:text-6xl
                  font-extrabold
                  leading-tight
                  text-gray-900
                "
              >
                Healthcare
                <span className="text-blue-600"> Made Connected</span>
              </h1>

              <p
                className="
                  mt-5
                  text-base
                  sm:text-lg
                  lg:text-xl
                  text-gray-600
                  leading-relaxed
                  max-w-2xl
                "
              >
                mrhealth.co.in is a digital healthcare platform designed to
                connect patients and doctors and make healthcare appointment
                management more convenient.
              </p>

              <p
                className="
                  mt-4
                  text-sm
                  sm:text-base
                  lg:text-lg
                  text-gray-600
                  leading-relaxed
                  max-w-2xl
                "
              >
                Our platform provides separate registration and secure login for
                patients and doctors, helping users manage their healthcare
                journey in an organized digital environment.
              </p>

              {/* =================================================
                  IMPORTANT NOTE
              ================================================= */}

              <div
                className="
                  mt-5
                  p-4
                  rounded-xl
                  bg-yellow-50
                  border
                  border-yellow-200
                "
              >
                <p
                  className="
                    text-sm
                    sm:text-base
                    text-yellow-800
                    leading-relaxed
                  "
                >
                  <strong>Important:</strong> Phone calls through the platform
                  are intended only for appointment-related communication.
                  mrhealth.co.in does not provide online medical treatment or
                  online consultation.
                </p>
              </div>

              {/* =================================================
                  BUTTONS
              ================================================= */}

              <div
                className="
                  flex
                  flex-col
                  sm:flex-row
                  gap-3
                  sm:gap-4
                  mt-7
                "
              >
                <Link
                  to="/"
                  className="
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    px-5
                    sm:px-6
                    py-3
                    bg-blue-600
                    text-white
                    rounded-xl
                    font-semibold
                    hover:bg-blue-700
                    transition
                    shadow-md
                  "
                >
                  Get Started
                  <ArrowRight className="w-5 h-5" />
                </Link>

                <a
                  href="#how-it-works"
                  className="
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    px-5
                    sm:px-6
                    py-3
                    bg-white
                    border
                    border-blue-200
                    text-blue-700
                    rounded-xl
                    font-semibold
                    hover:bg-blue-50
                    transition
                  "
                >
                  How It Works
                </a>
              </div>
            </div>

            {/* =================================================
                RIGHT SIDE
            ================================================= */}

            <div className="w-full min-w-0">
              <div className="relative w-full max-w-xl mx-auto lg:ml-auto">
                {/* Background Effects */}

                <div
                  className="
                    absolute
                    -top-8
                    -right-8
                    w-32
                    h-32
                    sm:w-40
                    sm:h-40
                    bg-blue-200
                    rounded-full
                    blur-3xl
                    opacity-60
                  "
                />

                <div
                  className="
                    absolute
                    -bottom-8
                    -left-8
                    w-32
                    h-32
                    sm:w-40
                    sm:h-40
                    bg-cyan-200
                    rounded-full
                    blur-3xl
                    opacity-60
                  "
                />

                {/* =================================================
                    MAIN CARD
                ================================================= */}

                <div
                  className="
                    relative
                    bg-white
                    rounded-2xl
                    sm:rounded-3xl
                    shadow-xl
                    border
                    border-blue-100
                    p-4
                    sm:p-6
                  "
                >
                  {/* =================================================
                      DOCTOR
                  ================================================= */}

                  <div
                    className="
                      flex
                      items-center
                      gap-3
                      sm:gap-4
                      p-3
                      sm:p-4
                      bg-blue-50
                      rounded-xl
                      sm:rounded-2xl
                    "
                  >
                    <div
                      className="
                        w-11
                        h-11
                        sm:w-14
                        sm:h-14
                        flex-shrink-0
                        rounded-full
                        bg-blue-600
                        flex
                        items-center
                        justify-center
                      "
                    >
                      <Stethoscope
                        className="
                          w-5
                          h-5
                          sm:w-7
                          sm:h-7
                          text-white
                        "
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900">Doctor</h3>

                      <p className="text-xs sm:text-sm text-gray-500">
                        Registered healthcare professional
                      </p>
                    </div>

                    <CheckCircle2
                      className="
                        w-5
                        h-5
                        text-green-500
                        flex-shrink-0
                      "
                    />
                  </div>

                  {/* =================================================
                      APPOINTMENT CONNECTION
                  ================================================= */}

                  <div className="flex justify-center py-4 sm:py-5">
                    <div className="flex flex-col items-center gap-2">
                      <div
                        className="
                          w-10
                          h-10
                          sm:w-12
                          sm:h-12
                          rounded-full
                          bg-cyan-100
                          flex
                          items-center
                          justify-center
                        "
                      >
                        <CalendarDays
                          className="
                            w-5
                            h-5
                            sm:w-6
                            sm:h-6
                            text-cyan-600
                          "
                        />
                      </div>

                      <span
                        className="
                          text-xs
                          sm:text-sm
                          font-medium
                          text-cyan-600
                        "
                      >
                        Appointment
                      </span>
                    </div>
                  </div>

                  {/* =================================================
                      PATIENT
                  ================================================= */}

                  <div
                    className="
                      flex
                      items-center
                      gap-3
                      sm:gap-4
                      p-3
                      sm:p-4
                      bg-cyan-50
                      rounded-xl
                      sm:rounded-2xl
                    "
                  >
                    <div
                      className="
                        w-11
                        h-11
                        sm:w-14
                        sm:h-14
                        flex-shrink-0
                        rounded-full
                        bg-cyan-600
                        flex
                        items-center
                        justify-center
                      "
                    >
                      <UserRound
                        className="
                          w-5
                          h-5
                          sm:w-7
                          sm:h-7
                          text-white
                        "
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900">Patient</h3>

                      <p className="text-xs sm:text-sm text-gray-500">
                        Find and manage doctor appointments
                      </p>
                    </div>

                    <CalendarDays
                      className="
                        w-5
                        h-5
                        sm:w-6
                        sm:h-6
                        flex-shrink-0
                        text-cyan-600
                      "
                    />
                  </div>

                  {/* =================================================
                      PHONE CALL INFORMATION
                  ================================================= */}

                  <div
                    className="
                      mt-4
                      flex
                      items-start
                      gap-3
                      p-3
                      sm:p-4
                      bg-green-50
                      border
                      border-green-100
                      rounded-xl
                    "
                  >
                    <Phone
                      className="
                        w-5
                        h-5
                        text-green-600
                        flex-shrink-0
                        mt-0.5
                      "
                    />

                    <p
                      className="
                        text-xs
                        sm:text-sm
                        text-gray-600
                        leading-relaxed
                      "
                    >
                      Phone communication may be used for
                      <strong className="text-green-700">
                        {" "}
                        appointment-related purposes only.
                      </strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            MISSION
        ====================================================== */}

        <section
          className="
            bg-white
            rounded-2xl
            sm:rounded-3xl
            shadow-sm
            border
            border-blue-100
            py-10
            sm:py-14
            lg:py-16
            px-5
            sm:px-8
            lg:px-12
          "
        >
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-4">
              <div
                className="
                  w-12
                  h-12
                  sm:w-14
                  sm:h-14
                  rounded-2xl
                  bg-blue-100
                  flex
                  items-center
                  justify-center
                "
              >
                <Target
                  className="
                    w-6
                    h-6
                    sm:w-7
                    sm:h-7
                    text-blue-600
                  "
                />
              </div>
            </div>

            <h2
              className="
                text-2xl
                sm:text-3xl
                md:text-4xl
                font-bold
                text-gray-900
              "
            >
              Our Mission
            </h2>

            <p
              className="
                mt-5
                text-base
                sm:text-lg
                text-gray-600
                leading-relaxed
              "
            >
              Our mission is to make healthcare appointment management more
              convenient, organized and accessible through technology. We aim to
              provide a simple digital environment where patients and doctors
              can manage their healthcare appointment journey.
            </p>
          </div>
        </section>

        {/* =====================================================
            PATIENT & DOCTOR
        ====================================================== */}

        <section className="py-12 sm:py-16 lg:py-20">
          <div className="text-center mb-8 sm:mb-12">
            <h2
              className="
                text-2xl
                sm:text-3xl
                md:text-4xl
                font-bold
                text-gray-900
              "
            >
              Built for Patients and Doctors
            </h2>

            <p className="mt-3 text-sm sm:text-base text-gray-600">
              A simple platform designed to support appointment management.
            </p>
          </div>

          <div
            className="
              grid
              grid-cols-1
              lg:grid-cols-2
              gap-5
              sm:gap-6
              lg:gap-8
            "
          >
            {/* =================================================
                PATIENT
            ================================================= */}

            <div
              className="
                bg-white
                rounded-2xl
                sm:rounded-3xl
                p-5
                sm:p-7
                lg:p-8
                shadow-lg
                border
                border-blue-100
                hover:shadow-xl
                transition
              "
            >
              <div
                className="
                  w-12
                  h-12
                  sm:w-14
                  sm:h-14
                  bg-blue-100
                  rounded-2xl
                  flex
                  items-center
                  justify-center
                "
              >
                <UserRound
                  className="
                    w-6
                    h-6
                    sm:w-7
                    sm:h-7
                    text-blue-600
                  "
                />
              </div>

              <h3
                className="
                  mt-5
                  text-xl
                  sm:text-2xl
                  font-bold
                  text-gray-900
                "
              >
                For Patients
              </h3>

              <p
                className="
                  mt-3
                  text-sm
                  sm:text-base
                  text-gray-600
                  leading-relaxed
                "
              >
                Patients can create an account, securely log in and use the
                platform to manage their doctor appointment requirements.
              </p>

              <div className="mt-6 space-y-4">
                {[
                  "Patient registration",
                  "Secure login",
                  "Find and manage appointments",
                  "Appointment-related phone communication",
                ].map((item, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <CheckCircle2
                      className="
                        w-5
                        h-5
                        text-green-600
                        flex-shrink-0
                        mt-0.5
                      "
                    />

                    <span className="text-sm sm:text-base">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* =================================================
                DOCTOR
            ================================================= */}

            <div
              className="
                bg-white
                rounded-2xl
                sm:rounded-3xl
                p-5
                sm:p-7
                lg:p-8
                shadow-lg
                border
                border-cyan-100
                hover:shadow-xl
                transition
              "
            >
              <div
                className="
                  w-12
                  h-12
                  sm:w-14
                  sm:h-14
                  bg-cyan-100
                  rounded-2xl
                  flex
                  items-center
                  justify-center
                "
              >
                <Stethoscope
                  className="
                    w-6
                    h-6
                    sm:w-7
                    sm:h-7
                    text-cyan-600
                  "
                />
              </div>

              <h3
                className="
                  mt-5
                  text-xl
                  sm:text-2xl
                  font-bold
                  text-gray-900
                "
              >
                For Doctors
              </h3>

              <p
                className="
                  mt-3
                  text-sm
                  sm:text-base
                  text-gray-600
                  leading-relaxed
                "
              >
                Doctors can register on the platform, maintain their profile and
                manage appointment-related activities.
              </p>

              <div className="mt-6 space-y-4">
                {[
                  "Doctor registration",
                  "Secure login",
                  "Manage doctor profile",
                  "Appointment-related phone communication",
                ].map((item, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <CheckCircle2
                      className="
                        w-5
                        h-5
                        text-green-600
                        flex-shrink-0
                        mt-0.5
                      "
                    />

                    <span className="text-sm sm:text-base">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            HOW IT WORKS
        ====================================================== */}

        <section
          id="how-it-works"
          className="
            bg-white
            rounded-2xl
            sm:rounded-3xl
            shadow-sm
            border
            border-blue-100
            py-10
            sm:py-14
            lg:py-16
            px-5
            sm:px-8
            lg:px-10
          "
        >
          <div className="text-center mb-8 sm:mb-12">
            <h2
              className="
                text-2xl
                sm:text-3xl
                md:text-4xl
                font-bold
                text-gray-900
              "
            >
              How It Works
            </h2>

            <p className="mt-3 text-sm sm:text-base text-gray-600">
              Getting started is simple for both doctors and patients.
            </p>
          </div>

          <div
            className="
              grid
              grid-cols-1
              sm:grid-cols-2
              lg:grid-cols-4
              gap-4
              sm:gap-6
            "
          >
            {/* REGISTER */}

            <div
              className="
                text-center
                p-5
                sm:p-6
                rounded-2xl
                bg-blue-50
                border
                border-blue-100
              "
            >
              <div
                className="
                  mx-auto
                  w-12
                  h-12
                  sm:w-14
                  sm:h-14
                  rounded-full
                  bg-blue-600
                  text-white
                  flex
                  items-center
                  justify-center
                "
              >
                <UserPlus className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>

              <h3 className="mt-5 font-bold text-base sm:text-lg">
                1. Register
              </h3>

              <p
                className="
                  mt-2
                  text-xs
                  sm:text-sm
                  text-gray-600
                  leading-relaxed
                "
              >
                Doctors and patients create their respective accounts.
              </p>
            </div>

            {/* LOGIN */}

            <div
              className="
                text-center
                p-5
                sm:p-6
                rounded-2xl
                bg-cyan-50
                border
                border-cyan-100
              "
            >
              <div
                className="
                  mx-auto
                  w-12
                  h-12
                  sm:w-14
                  sm:h-14
                  rounded-full
                  bg-cyan-600
                  text-white
                  flex
                  items-center
                  justify-center
                "
              >
                <LogIn className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>

              <h3 className="mt-5 font-bold text-base sm:text-lg">2. Login</h3>

              <p
                className="
                  mt-2
                  text-xs
                  sm:text-sm
                  text-gray-600
                  leading-relaxed
                "
              >
                Registered users securely log in using their credentials.
              </p>
            </div>

            {/* APPOINTMENT */}

            <div
              className="
                text-center
                p-5
                sm:p-6
                rounded-2xl
                bg-green-50
                border
                border-green-100
              "
            >
              <div
                className="
                  mx-auto
                  w-12
                  h-12
                  sm:w-14
                  sm:h-14
                  rounded-full
                  bg-green-600
                  text-white
                  flex
                  items-center
                  justify-center
                "
              >
                <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>

              <h3 className="mt-5 font-bold text-base sm:text-lg">
                3. Appointment
              </h3>

              <p
                className="
                  mt-2
                  text-xs
                  sm:text-sm
                  text-gray-600
                  leading-relaxed
                "
              >
                Patients and doctors can manage appointment-related activities.
              </p>
            </div>

            {/* PHONE */}

            <div
              className="
                text-center
                p-5
                sm:p-6
                rounded-2xl
                bg-sky-50
                border
                border-sky-100
              "
            >
              <div
                className="
                  mx-auto
                  w-12
                  h-12
                  sm:w-14
                  sm:h-14
                  rounded-full
                  bg-sky-600
                  text-white
                  flex
                  items-center
                  justify-center
                "
              >
                <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>

              <h3 className="mt-5 font-bold text-base sm:text-lg">
                4. Appointment Call
              </h3>

              <p
                className="
                  mt-2
                  text-xs
                  sm:text-sm
                  text-gray-600
                  leading-relaxed
                "
              >
                Phone calls may be used for appointment-related communication
                only.
              </p>
            </div>
          </div>
        </section>

        {/* =====================================================
            FEATURES
        ====================================================== */}

        <section className="py-12 sm:py-16 lg:py-20">
          <div className="text-center mb-8 sm:mb-12">
            <h2
              className="
                text-2xl
                sm:text-3xl
                md:text-4xl
                font-bold
                text-gray-900
              "
            >
              Why Choose mrhealth.co.in?
            </h2>

            <p className="mt-3 text-sm sm:text-base text-gray-600">
              Designed to make healthcare appointment management simple.
            </p>
          </div>

          <div
            className="
              grid
              grid-cols-1
              sm:grid-cols-2
              lg:grid-cols-4
              gap-4
              sm:gap-6
            "
          >
            {/* FEATURE 1 */}

            <div
              className="
                bg-white
                p-5
                sm:p-6
                rounded-2xl
                shadow-md
                border
                border-blue-100
              "
            >
              <CalendarDays
                className="
                  w-8
                  h-8
                  sm:w-9
                  sm:h-9
                  text-blue-600
                "
              />

              <h3 className="mt-4 font-bold text-base sm:text-lg">
                Appointment Management
              </h3>

              <p
                className="
                  mt-2
                  text-sm
                  text-gray-600
                  leading-relaxed
                "
              >
                Make healthcare appointment scheduling more organized and
                convenient.
              </p>
            </div>

            {/* FEATURE 2 */}

            <div
              className="
                bg-white
                p-5
                sm:p-6
                rounded-2xl
                shadow-md
                border
                border-cyan-100
              "
            >
              <Phone
                className="
                  w-8
                  h-8
                  sm:w-9
                  sm:h-9
                  text-cyan-600
                "
              />

              <h3 className="mt-4 font-bold text-base sm:text-lg">
                Appointment Calls
              </h3>

              <p
                className="
                  mt-2
                  text-sm
                  text-gray-600
                  leading-relaxed
                "
              >
                Support phone communication between patients and doctors for
                appointment-related purposes.
              </p>
            </div>

            {/* FEATURE 3 */}

            <div
              className="
                bg-white
                p-5
                sm:p-6
                rounded-2xl
                shadow-md
                border
                border-green-100
              "
            >
              <ShieldCheck
                className="
                  w-8
                  h-8
                  sm:w-9
                  sm:h-9
                  text-green-600
                "
              />

              <h3 className="mt-4 font-bold text-base sm:text-lg">
                Privacy Focused
              </h3>

              <p
                className="
                  mt-2
                  text-sm
                  text-gray-600
                  leading-relaxed
                "
              >
                Designed with account privacy and controlled access in mind.
              </p>
            </div>

            {/* FEATURE 4 */}

            <div
              className="
                bg-white
                p-5
                sm:p-6
                rounded-2xl
                shadow-md
                border
                border-sky-100
              "
            >
              <ClipboardList
                className="
                  w-8
                  h-8
                  sm:w-9
                  sm:h-9
                  text-sky-600
                "
              />

              <h3 className="mt-4 font-bold text-base sm:text-lg">
                Organized Healthcare
              </h3>

              <p
                className="
                  mt-2
                  text-sm
                  text-gray-600
                  leading-relaxed
                "
              >
                Keep registration, profiles and appointment-related activities
                organized in one platform.
              </p>
            </div>
          </div>
        </section>

        {/* =====================================================
            IMPORTANT INFORMATION
        ====================================================== */}

        <section
          className="
            rounded-2xl
            sm:rounded-3xl
            bg-gradient-to-r
            from-blue-600
            via-cyan-600
            to-teal-500
            py-10
            sm:py-14
            lg:py-16
            px-5
            sm:px-8
            lg:px-12
            text-white
          "
        >
          <div className="max-w-4xl mx-auto text-center">
            <ShieldCheck
              className="
                w-10
                h-10
                sm:w-12
                sm:h-12
                mx-auto
                mb-5
              "
            />

            <h2
              className="
                text-2xl
                sm:text-3xl
                md:text-4xl
                font-bold
              "
            >
              Privacy & Responsible Use
            </h2>

            <p
              className="
                mt-5
                text-sm
                sm:text-base
                lg:text-lg
                text-blue-100
                leading-relaxed
              "
            >
              We understand that healthcare information is personal and
              important. The platform is designed with secure account access and
              responsible handling of user information in mind.
            </p>

            <div
              className="
                mt-6
                p-4
                sm:p-5
                bg-white/10
                rounded-xl
                border
                border-white/20
              "
            >
              <p
                className="
                  text-sm
                  sm:text-base
                  lg:text-lg
                  leading-relaxed
                "
              >
                <strong>Important:</strong> mrhealth.co.in is intended for
                healthcare appointment management. Phone calls are allowed for
                appointment-related communication only. The platform does not
                provide online medical treatment or online medical consultation.
              </p>
            </div>
          </div>
        </section>

        {/* =====================================================
            FOUNDER
        ====================================================== */}

        <section className="py-12 sm:py-16 lg:py-20">
          <div className="text-center mb-8 sm:mb-10">
            <div className="flex justify-center mb-4">
              <div
                className="
                  w-12
                  h-12
                  sm:w-14
                  sm:h-14
                  bg-cyan-100
                  rounded-2xl
                  flex
                  items-center
                  justify-center
                "
              >
                <Users
                  className="
                    w-6
                    h-6
                    sm:w-7
                    sm:h-7
                    text-cyan-600
                  "
                />
              </div>
            </div>

            <h2
              className="
                text-2xl
                sm:text-3xl
                md:text-4xl
                font-bold
              "
            >
              Meet Our Founder
            </h2>
          </div>

          <div
            className="
              max-w-md
              mx-auto
              bg-white
              rounded-2xl
              sm:rounded-3xl
              shadow-lg
              border
              border-cyan-100
              p-6
              sm:p-8
              text-center
            "
          >
            <div
              className="
                w-16
                h-16
                sm:w-20
                sm:h-20
                mx-auto
                rounded-full
                bg-gradient-to-br
                from-blue-600
                to-cyan-600
                flex
                items-center
                justify-center
                text-white
              "
            >
              <UserRound
                className="
                  w-8
                  h-8
                  sm:w-10
                  sm:h-10
                "
              />
            </div>

            {founders.map((person, index) => (
              <div key={index}>
                <h3
                  className="
                    mt-5
                    text-xl
                    sm:text-2xl
                    font-bold
                    text-gray-900
                    break-words
                  "
                >
                  {person.name}
                </h3>

                <p className="mt-1 text-cyan-600 font-semibold">
                  {person.role}
                </p>

                <p
                  className="
                    mt-4
                    text-sm
                    sm:text-base
                    text-gray-600
                    leading-relaxed
                  "
                >
                  Building technology to create a more organized and convenient
                  healthcare appointment experience.
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* =====================================================
            MOBILE APP
        ====================================================== */}

        <section
          className="
            bg-white
            rounded-2xl
            sm:rounded-3xl
            shadow-sm
            border
            border-blue-100
            py-10
            sm:py-14
            lg:py-16
            px-5
            sm:px-8
          "
        >
          <div className="text-center">
            <Smartphone
              className="
                w-10
                h-10
                sm:w-12
                sm:h-12
                mx-auto
                text-blue-600
                mb-4
              "
            />

            <h2
              className="
                text-2xl
                sm:text-3xl
                font-bold
                text-gray-900
              "
            >
              Coming Soon on Mobile
            </h2>

            <p className="mt-3 text-sm sm:text-base text-gray-600">
              Manage your healthcare appointments conveniently on the go.
            </p>

            <div
              className="
                flex
                flex-col
                sm:flex-row
                justify-center
                gap-3
                sm:gap-4
                mt-8
              "
            >
              {/* =================================================
                  GOOGLE PLAY
              ================================================= */}

              <a
                href="https://play.google.com/store"
                target="_blank"
                rel="noopener noreferrer"
                className="
                  flex
                  items-center
                  justify-center
                  gap-3
                  px-5
                  sm:px-6
                  py-3
                  bg-black
                  text-white
                  rounded-xl
                  hover:bg-gray-800
                  transition
                "
              >
                <FaGooglePlay size={22} />

                <span className="text-left">
                  <span className="block text-[10px] sm:text-xs">
                    GET IT ON
                  </span>

                  <span className="font-semibold text-sm sm:text-base">
                    Google Play
                  </span>
                </span>
              </a>

              {/* =================================================
                  APP STORE
              ================================================= */}

              <a
                href="https://www.apple.com/app-store/"
                target="_blank"
                rel="noopener noreferrer"
                className="
                  flex
                  items-center
                  justify-center
                  gap-3
                  px-5
                  sm:px-6
                  py-3
                  bg-black
                  text-white
                  rounded-xl
                  hover:bg-gray-800
                  transition
                "
              >
                <FaApple size={25} />

                <span className="text-left">
                  <span className="block text-[10px] sm:text-xs">
                    DOWNLOAD ON THE
                  </span>

                  <span className="font-semibold text-sm sm:text-base">
                    App Store
                  </span>
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* =====================================================
            CONTACT
        ====================================================== */}

        <section className="py-10 sm:py-12">
          <div
            className="
              bg-gray-900
              rounded-2xl
              sm:rounded-3xl
              px-5
              sm:px-8
              py-8
              sm:py-10
              text-white
              text-center
            "
          >
            <Mail
              className="
                w-7
                h-7
                sm:w-8
                sm:h-8
                mx-auto
                mb-4
              "
            />

            <h2 className="text-xl sm:text-2xl font-bold">Have Questions?</h2>

            <p className="mt-2 text-sm sm:text-base text-gray-400">
              Get in touch with the mrhealth.co.in team.
            </p>

            <a
              href="mailto:httpsmrhealth.co.in@gmail.com"
              className="
                inline-block
                mt-4
                text-sm
                sm:text-base
                text-blue-400
                hover:text-blue-300
                hover:underline
                font-medium
                break-all
              "
            >
              httpsmrhealth.co.in@gmail.com
            </a>
          </div>
        </section>

        {/* =====================================================
            FOOTER
        ====================================================== */}

        <footer className="text-gray-500 text-center py-5">
          <p className="text-xs sm:text-sm">
            © {new Date().getFullYear()} mrhealth.co.in. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default AboutUs;
